import React, { useState, useEffect, useCallback } from 'react';
import { Calendar, Share2, X, Download, Link2, ExternalLink } from 'lucide-react';
import { type Address } from '../shared/types';

interface CalendarLinkProps {
  addresses: Address[];
  className?: string;
}

async function fetchCalendarId(addresses: Address[]): Promise<string> {
  if (addresses.length === 0) throw new Error('No addresses selected');

  const deploymentUrl = `${window.location.protocol}//${window.location.host}`;
  const response = await fetch(`${deploymentUrl}/api/calendar`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ addresses }),
  });

  if (!response.ok) {
    const errorText = await response.text();
    console.error('Server error response:', errorText);
    throw new Error(`Server error: ${response.status}`);
  }

  const data = await response.json();
  if (!data.id) throw new Error('Invalid response from calendar API');
  return data.id;
}

const CalendarLink: React.FC<CalendarLinkProps> = ({ addresses, className }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [shareUrl, setShareUrl] = useState<string | null>(null);
  const [showSharePopup, setShowSharePopup] = useState(false);

  const closePopup = useCallback(() => {
    setShowSharePopup(false);
  }, []);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && showSharePopup) {
        closePopup();
      }
    };

    if (showSharePopup) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [showSharePopup, closePopup]);

  const handleSubscribe = async () => {
    try {
      setIsLoading(true);
      const id = await fetchCalendarId(addresses);
      window.location.href = `webcal://${window.location.host}/api/calendar/${id}`;
    } catch (error) {
      console.error('Error generating calendar link:', error);
      alert(error instanceof Error ? error.message : 'Failed to generate calendar');
    } finally {
      setIsLoading(false);
    }
  };

  const handleShare = async () => {
    try {
      setIsLoading(true);
      const id = await fetchCalendarId(addresses);
      const deploymentUrl = `${window.location.protocol}//${window.location.host}`;
      setShareUrl(`${deploymentUrl}/api/calendar/${id}`);
      setShowSharePopup(true);
    } catch (error) {
      console.error('Error generating share link:', error);
      alert(error instanceof Error ? error.message : 'Failed to generate share link');
    } finally {
      setIsLoading(false);
    }
  };

  const copyToClipboard = async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      alert('Link copied to clipboard!');
    } catch {
      alert('Failed to copy link');
    }
  };

  return (
    <div className="relative">
      <div className="flex gap-2">
        <button
          onClick={handleSubscribe}
          disabled={isLoading}
          className={className || "btn-brutalist btn-mta-yellow flex items-center justify-center gap-2"}
        >
          <Calendar className="w-4 h-4" strokeWidth={3} />
          <span className="uppercase font-bold tracking-wide">
            {isLoading ? 'Loading...' : 'Add to Calendar'}
          </span>
        </button>
        <button
          onClick={handleShare}
          disabled={isLoading}
          className="btn-brutalist bg-mta-gray text-mta-white border-3 border-mta-black flex items-center justify-center w-12"
        >
          <Share2 className="w-4 h-4" strokeWidth={3} />
        </button>
      </div>

      {showSharePopup && shareUrl && (
        <div
          className="fixed inset-0 bg-mta-black/90 flex items-center justify-center z-50 p-4"
          onClick={(e) => {
            if (e.target === e.currentTarget) closePopup();
          }}
        >
          <div className="bg-mta-black border-3 border-mta-white p-6 max-w-md w-full" style={{ boxShadow: '8px 8px 0px 0px #FCCC0A' }}>
            <div className="flex justify-between items-center mb-6 pb-4 border-b-2 border-mta-white">
              <h3 className="text-lg font-bold text-mta-white uppercase tracking-wide font-helvetica">
                Subscribe to Calendar
              </h3>
              <button
                onClick={closePopup}
                className="text-mta-gray hover:text-mta-white transition-colors"
              >
                <X className="w-6 h-6" strokeWidth={3} />
              </button>
            </div>
            
            <div className="grid grid-cols-2 gap-3 mb-6">
              <button
                onClick={() => {
                  const webcalUrl = `webcal://${window.location.host}/api/calendar/${shareUrl.split('/').pop()}`;
                  window.location.href = webcalUrl;
                }}
                className="btn-brutalist btn-mta-blue !px-3 !py-3 text-xs flex flex-col items-center gap-2"
              >
                <Calendar className="w-5 h-5" />
                <span>Apple Calendar</span>
              </button>
              
              <button
                onClick={() => {
                  const calUrl = encodeURIComponent(shareUrl);
                  window.open(`https://outlook.live.com/calendar/addcalendar?url=${calUrl}&name=NYC%20ASP%20Schedule`, '_blank');
                }}
                className="btn-brutalist bg-mta-purple text-mta-white border-3 border-mta-black !px-3 !py-3 text-xs flex flex-col items-center gap-2"
              >
                <ExternalLink className="w-5 h-5" />
                <span>Outlook</span>
              </button>
              
              <button
                onClick={() => {
                  window.open(shareUrl, '_blank');
                }}
                className="btn-brutalist bg-mta-gray text-mta-white border-3 border-mta-black !px-3 !py-3 text-xs flex flex-col items-center gap-2"
              >
                <Download className="w-5 h-5" />
                <span>Download .ics</span>
              </button>
              
              <button
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(shareUrl);
                    alert('URL copied! For Google Calendar:\n\n1. Go to calendar.google.com\n2. Click + next to "Other calendars"\n3. Select "From URL"\n4. Paste the URL\n5. Click "Add Calendar"');
                  } catch (err) {
                    alert('Please copy the URL below manually');
                  }
                }}
                className="btn-brutalist btn-mta-green !px-3 !py-3 text-xs flex flex-col items-center gap-2"
              >
                <Link2 className="w-5 h-5" />
                <span>Google (Manual)</span>
              </button>
            </div>
            
            <div className="border-t-2 border-mta-white pt-4">
              <p className="text-mta-silver text-xs uppercase tracking-wide mb-3">
                Or copy URL manually:
              </p>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={shareUrl}
                  readOnly
                  className="input-brutalist flex-1 text-xs !py-2"
                  onClick={(e) => e.currentTarget.select()}
                />
                <button
                  onClick={copyToClipboard}
                  className="btn-brutalist btn-mta-yellow !py-2 !px-4 text-xs"
                >
                  Copy
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CalendarLink;
